/**
 * ============================================================================
 * LOADING COMPONENT v3.0 - PLANIVIO REBRANDING
 * ============================================================================
 * 
 * Features:
 * ✅ Loading - Einfacher Spinner (Original, für Rückwärtskompatibilität)
 * ✅ LoadingOverlay - Content bleibt sichtbar, nur ausgegraut
 * ✅ LoadingIndicator - Kleiner Spinner für Header/Ecke
 * ✅ Skeleton - Graue animierte Placeholder
 * ✅ SkeletonCard - Card-förmiger Placeholder
 * 
 * ✅ v3.0: Planivio Rebranding
 *          - Farben: Orange (#F97316) → Teal (#4A90A4)
 * 
 * @version 3.0.0 - Planivio Rebranding
 */

import React, { useState, useEffect } from 'react';

console.log('⏳ Loading.js v3.0 loaded - PLANIVIO REBRANDING');

// ============================================================================
// ORIGINAL LOADING COMPONENT (Rückwärtskompatibilität)
// ============================================================================
const Loading = ({ 
  icon = '🏠', 
  text = 'Laden...'
}) => {
  
  // ✅ v3: Planivio Styles
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      background: 'white',
      borderRadius: '16px',
      // ✅ v3: Planivio Teal border & shadow
      border: '2px solid #B8D4DC',
      boxShadow: '0 4px 20px rgba(74, 144, 164, 0.1)',
      gap: '1rem'
    },
    icon: {
      fontSize: '3rem'
    },
    spinner: {
      width: '40px',
      height: '40px',
      // ✅ v3: Planivio Teal spinner
      border: '4px solid #B8D4DC',
      borderTop: '4px solid #4A90A4',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    text: {
      color: '#6B7280',
      margin: 0,
      fontSize: '1rem'
    }
  };

  return (
    <div style={styles.container}>
      <span style={styles.icon}>{icon}</span>
      <div style={styles.spinner}></div>
      <p style={styles.text}>{text}</p>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// LOADING OVERLAY - Content bleibt sichtbar, nur ausgegraut
// ============================================================================
export function LoadingOverlay({ 
  isLoading, 
  children, 
  delay = 200,      // Delay in ms bevor Loading erscheint
  opacity = 0.6,    // Wie stark ausgegraut
  showSpinner = true,
  spinnerText = 'Laden...'
}) {
  const [showLoading, setShowLoading] = useState(false);
  
  useEffect(() => {
    let timer;
    
    if (isLoading) {
      // Zeige Loading erst nach Delay (verhindert Flackern bei schnellen Responses)
      timer = setTimeout(() => {
        setShowLoading(true);
      }, delay);
    } else {
      setShowLoading(false);
    }
    
    return () => clearTimeout(timer);
  }, [isLoading, delay]);
  
  return (
    <div style={{ position: 'relative', minHeight: '100px' }}>
      {/* Content bleibt IMMER sichtbar, nur ausgegraut beim Laden */}
      <div style={{ 
        opacity: showLoading ? opacity : 1,
        transition: 'opacity 0.2s ease',
        pointerEvents: showLoading ? 'none' : 'auto'
      }}>
        {children}
      </div>
      
      {/* Loading Spinner als Overlay */}
      {showLoading && showSpinner && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '20px 30px',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          zIndex: 10,
          // ✅ v3: Planivio Teal border
          border: '2px solid #B8D4DC'
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            // ✅ v3: Planivio Teal spinner
            border: '3px solid #B8D4DC',
            borderTop: '3px solid #4A90A4',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span style={{ 
            color: '#374151', 
            fontWeight: 500,
            fontSize: '0.95rem'
          }}>
            {spinnerText}
          </span>
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// LOADING INDICATOR - Kleiner Spinner für Header/Ecke
// ============================================================================
export function LoadingIndicator({ 
  isLoading = true,
  text = 'Aktualisiere...',
  size = 16,
  position = 'fixed',  // 'fixed' oder 'inline'
  style = {}
}) {
  if (!isLoading) return null;
  
  const baseStyle = position === 'fixed' ? {
    position: 'fixed',
    top: '80px',
    right: '20px',
    background: 'white',
    padding: '8px 16px',
    borderRadius: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.85rem',
    color: '#374151',
    zIndex: 100,
    // ✅ v3: Planivio Teal border
    border: '1px solid #B8D4DC',
    ...style
  } : {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.85rem',
    color: '#6B7280',
    ...style
  };
  
  return (
    <div style={baseStyle}>
      <div style={{
        width: size,
        height: size,
        // ✅ v3: Planivio Teal spinner
        border: '2px solid #B8D4DC',
        borderTop: '2px solid #4A90A4',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      {text && <span>{text}</span>}
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// SKELETON - Graue animierte Placeholder
// ============================================================================
export function Skeleton({ 
  width = '100%', 
  height = 20, 
  borderRadius = 8,
  style = {} 
}) {
  return (
    <div style={{
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
      borderRadius,
      background: 'linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 50%, #F3F4F6 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.5s infinite',
      ...style
    }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// SKELETON CARD - Card-förmiger Placeholder
// ============================================================================
export function SkeletonCard({ 
  height = 150, 
  showHeader = true,
  showFooter = true,
  style = {} 
}) {
  return (
    <div style={{
      background: '#FFFFFF',
      borderRadius: '16px',
      padding: '20px',
      // ✅ v3: Planivio Teal border & shadow
      border: '2px solid #B8D4DC',
      boxShadow: '0 2px 8px rgba(74, 144, 164, 0.05)',
      ...style
    }}>
      {showHeader && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <Skeleton width={40} height={40} borderRadius={10} />
          <div style={{ flex: 1 }}>
            <Skeleton width="60%" height={18} style={{ marginBottom: '8px' }} />
            <Skeleton width="40%" height={14} />
          </div>
        </div>
      )}
      
      <Skeleton 
        width="100%" 
        height={height - (showHeader ? 80 : 0) - (showFooter ? 40 : 0)} 
        borderRadius={12}
        style={{ marginBottom: showFooter ? '16px' : 0 }} 
      />
      
      {showFooter && (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Skeleton width="30%" height={16} />
          <Skeleton width="20%" height={16} />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SKELETON LIST - Mehrere Skeleton Items
// ============================================================================
export function SkeletonList({ 
  count = 3, 
  itemHeight = 60,
  gap = 12,
  style = {}
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap, ...style }}>
      {Array.from({ length: count }).map((_, i) => (
        <div 
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #F3F4F6'
          }}
        >
          <Skeleton width={36} height={36} borderRadius={8} />
          <div style={{ flex: 1 }}>
            <Skeleton width="70%" height={16} style={{ marginBottom: '6px' }} />
            <Skeleton width="40%" height={12} />
          </div>
          <Skeleton width={60} height={24} borderRadius={6} />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// SKELETON GRID - Grid von Skeleton Cards
// ============================================================================
export function SkeletonGrid({ 
  count = 4, 
  columns = 'repeat(auto-fill, minmax(280px, 1fr))',
  cardHeight = 180,
  gap = 16,
  style = {}
}) {
  return (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: columns, 
      gap,
      ...style 
    }}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} height={cardHeight} />
      ))}
    </div>
  );
}

// ============================================================================
// LOADING DOTS - Animierte Punkte "..."
// ============================================================================
// ✅ v3: Planivio Teal as default color
export function LoadingDots({ color = '#4A90A4', size = 8 }) {
  return (
    <span style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: color,
            animation: `bounce 1.4s ease-in-out ${i * 0.16}s infinite both`
          }}
        />
      ))}
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </span>
  );
}

// ============================================================================
// EMPTY STATE - Wenn keine Daten vorhanden
// ============================================================================
export function EmptyState({
  icon = '📭',
  title = 'Keine Daten',
  description = 'Es sind noch keine Einträge vorhanden.',
  actionText,
  onAction,
  style = {}
}) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      background: 'white',
      borderRadius: '16px',
      // ✅ v3: Planivio Teal border
      border: '2px dashed #B8D4DC',
      textAlign: 'center',
      ...style
    }}>
      <span style={{ fontSize: '3rem', marginBottom: '16px' }}>{icon}</span>
      <h3 style={{ 
        margin: '0 0 8px', 
        color: '#374151',
        fontSize: '1.25rem',
        fontWeight: 600
      }}>
        {title}
      </h3>
      <p style={{ 
        margin: '0 0 20px', 
        color: '#6B7280',
        fontSize: '0.95rem',
        maxWidth: '300px'
      }}>
        {description}
      </p>
      
      {actionText && onAction && (
        <button
          onClick={onAction}
          style={{
            padding: '10px 24px',
            // ✅ v3: Planivio Teal gradient
            background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 15px rgba(74, 144, 164, 0.3)'
          }}
        >
          {actionText}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// ERROR STATE - Bei Fehlern
// ============================================================================
export function ErrorState({
  icon = '⚠️',
  title = 'Fehler aufgetreten',
  description = 'Etwas ist schief gelaufen.',
  onRetry,
  style = {}
}) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      background: '#FEF2F2',
      borderRadius: '16px',
      border: '2px solid #FECACA',
      textAlign: 'center',
      ...style
    }}>
      <span style={{ fontSize: '3rem', marginBottom: '16px' }}>{icon}</span>
      <h3 style={{ 
        margin: '0 0 8px', 
        color: '#991B1B',
        fontSize: '1.25rem',
        fontWeight: 600
      }}>
        {title}
      </h3>
      <p style={{ 
        margin: '0 0 20px', 
        color: '#B91C1C',
        fontSize: '0.95rem',
        maxWidth: '300px'
      }}>
        {description}
      </p>
      
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            padding: '10px 24px',
            // ✅ v3: Planivio Teal gradient for retry
            background: 'linear-gradient(135deg, #4A90A4, #6BA893)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 15px rgba(74, 144, 164, 0.3)'
          }}
        >
          🔄 Erneut versuchen
        </button>
      )}
    </div>
  );
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================
export default Loading;